import { MultipleChoices } from "@/components/PredictionMarket/MarketCardList/MultipleChoices";
import { useHandleError } from "@/hooks/useHandleError";
import type { PredictionMarketWithProbability } from "@roman/shared/types/PredictionMarket";
import { PrimaryButton } from "../Buttons/PrimaryButton";

interface EntrySharePopupProps {
  market: PredictionMarketWithProbability;
  onClose: () => void;
}

export const EntrySharePopup: React.FC<EntrySharePopupProps> = ({ market, onClose }) => {
  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: market.title,
          url: `${window.location.origin}/prediction-market/details/${market.id}`,
        })
        .catch((error) => {
          useHandleError("Error when sharing.")(error);
        });
    } else {
      // Fallback: copy to clipboard
      const url = `${window.location.origin}/prediction-market/details/${market.id}`;
      navigator.clipboard
        .writeText(url)
        .then(() => {
          window.alert("URLをクリップボードにコピーしました！");
        })
        .catch(() => {
          window.alert("Sharing is not supported on this device.");
        });
    }
  };

  return (
    <div className="w-full flex flex-col p-[8px]">
      <div
        className="relative w-full rounded-lg overflow-hidden flex flex-col mx-auto items-center"
        style={{
          background: "linear-gradient(90deg, #01A4F9 0%, #176BF2 100%)",
        }}
      >
        {/* Logo and subtitle centered at the top */}
        <div className="flex flex-col items-center pt-2.5">
          <img src="/assets/logo-only-white.svg" alt="Samba logo" className="h-5" />
          <span className="text-white pt-1 pb-2 font-bold" style={{ fontSize: "9.75px" }}>
            "未来予想で、ちょい稼げる、新感覚ポイ活"
          </span>
        </div>

        {/* Watermark logo at top right */}
        <img
          src="/assets/thumba-logo-transparent.svg"
          alt=""
          className="absolute top-0 right-0 w-[150px] pointer-events-none select-none"
          aria-hidden="true"
        />

        {/* Flexible content area with image and choices */}
        <div className="flex-1 flex flex-col items-center justify-center w-[326.8px]">
          {/* Image (outside the white card) */}
          {market.primaryImageURL && (
            <img
              src={market.primaryImageURL}
              alt={market.title}
              className="w-full h-[93.77px] object-cover rounded-t mb-0"
            />
          )}
          {/* White card with content */}
          <div
            className={`w-full bg-white rounded-b shadow-lg py-1.5 px-3 mb-2 flex flex-col items-center ${!market.primaryImageURL && "rounded"}`}
          >
            {/* Title */}
            <div className="font-bold text-left text-base mb-2 w-full h-[48px] overflow-hidden line-clamp-2">
              {market.title}
            </div>
            {/* Choices using MultipleChoices */}
            <div className="w-full h-auto">
              <MultipleChoices
                choices={market.predictionMarketChoices}
                onButtonClick={() => {}}
                outlined={false}
                onlyProbability
                showTop
              />
            </div>
            <p className="text-xs text-primary font-bold pt-2">予想を当てて、ギフト券やポイントをゲットしよう！</p>
          </div>
        </div>

        {/* Decorative line at the bottom
        <div className="w-full flex justify-center my-8">
          <svg
            width="100%"
            height="6"
            viewBox="0 0 344 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-1.5"
          >
            <line
              x1="-750"
              y1="3"
              x2="600"
              y2="3"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="12 8"
            />
          </svg>
        </div> */}
      </div>
      {/* Action buttons below the card */}
      <div className="w-full my-4">
        <PrimaryButton content="シェアする" onClick={handleShare} className="w-full" />
      </div>
      <div className="text-center text-sm text-primary underline cursor-pointer mb-4" onClick={onClose}>
        閉じる
      </div>
    </div>
  );
};
